import api from './client';
import type { InventoryItem, InventoryUnit, Category, ItemAccessory } from '@/types';

export const inventoryApi = {
  list: () => api.get<InventoryItem[]>('/inventory').then((r) => r.data),
  get:  (id: string) => api.get<InventoryItem>(`/inventory/${id}`).then((r) => r.data),
  history: (id: string) => api.get(`/inventory/${id}/history`).then((r) => r.data),
  // Per-item loss timeline (every "marked missing" event, bulk or tracked).
  // Each row: { id, createdAt, quantityLost, description, lostOnJob, inventoryUnit }
  lossHistory: (id: string) => api.get(`/inventory/${id}/loss-history`).then((r) => r.data),
  availability: (id: string, start: string, end: string) =>
    api.get<{ availableQuantity: number }>(`/inventory/${id}/availability`, { params: { start, end } }).then((r) => r.data),
  // Batched: availability for EVERY item in one call → { [itemId]: availableQty }
  availabilityMap: (start: string, end: string) =>
    api.get<Record<string, number>>('/inventory/availability', { params: { start, end } }).then((r) => r.data),
  create: (data: Partial<InventoryItem>) => api.post<InventoryItem>('/inventory', data).then((r) => r.data),
  update: (id: string, data: Partial<InventoryItem>) => api.patch<InventoryItem>(`/inventory/${id}`, data).then((r) => r.data),
  retire: (id: string) => api.delete(`/inventory/${id}`).then((r) => r.data),
  hardDelete: (id: string) => api.delete(`/inventory/${id}`, { params: { permanent: 'true' } }).then((r) => r.data),

  // Units
  listUnits: (itemId: string) => api.get<InventoryUnit[]>(`/inventory/${itemId}/units`).then((r) => r.data),
  createUnit: (itemId: string, data: Partial<InventoryUnit>) =>
    api.post<InventoryUnit>(`/inventory/${itemId}/units`, data).then((r) => r.data),
  updateUnit: (itemId: string, unitId: string, data: Partial<InventoryUnit>) =>
    api.patch<InventoryUnit>(`/inventory/${itemId}/units/${unitId}`, data).then((r) => r.data),
  deleteUnit: (itemId: string, unitId: string) =>
    api.delete(`/inventory/${itemId}/units/${unitId}`).then((r) => r.data),
  enableSubUnits: (itemId: string, assetId?: string) =>
    api.post<{ groupAssetId: string; alreadyEnabled: boolean }>(
      `/inventory/${itemId}/enable-sub-units`,
      assetId ? { assetId } : {},
    ).then((r) => r.data),
  // Combined timeline (tests + checkouts + maintenance + audit) for a unit
  // by its asset ID. Backs the /inventory/units/:assetId page and the
  // Cmd-K palette's deep links.
  unitHistory: (assetId: string) =>
    api.get(`/inventory/by-asset/${encodeURIComponent(assetId)}/history`).then((r) => r.data),

  // Accessories (warehouse fixture BOM — clamps, safeties, leads)
  listAccessories: (itemId: string) =>
    api.get<ItemAccessory[]>(`/inventory/${itemId}/accessories`).then((r) => r.data),
  createAccessory: (itemId: string, data: Partial<ItemAccessory>) =>
    api.post<ItemAccessory>(`/inventory/${itemId}/accessories`, data).then((r) => r.data),
  updateAccessory: (itemId: string, accId: string, data: Partial<ItemAccessory>) =>
    api.patch<ItemAccessory>(`/inventory/${itemId}/accessories/${accId}`, data).then((r) => r.data),
  deleteAccessory: (itemId: string, accId: string) =>
    api.delete(`/inventory/${itemId}/accessories/${accId}`).then((r) => r.data),

  // Categories
  listCategories: () => api.get<Category[]>('/categories').then((r) => r.data),
  createCategory: (data: { name: string; sortOrder?: number }) =>
    api.post<Category>('/categories', data).then((r) => r.data),
  updateCategory: (id: string, data: Partial<Category>) =>
    api.patch<Category>(`/categories/${id}`, data).then((r) => r.data),
  deleteCategory: (id: string) => api.delete(`/categories/${id}`).then((r) => r.data),
};
